import React from 'react'
import classNames from 'classnames'
import { ParagraphLG, ParagraphSM, TitleLG } from '@/atoms/Text'
import { useIsDarkMode } from '@/contexts/ThemeContext'
import type { TrackerResult } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'

interface GoalTrackerDisplayProps {
  tracker: TrackerResult
  title?: string
}

export function formatTrackerValue(value: number, units: string, locale: string): string {
  if (units === 'USD_CENTS') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value / 100)
  }
  if (units === 'USD') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value)
  }
  return new Intl.NumberFormat(locale).format(value)
}

export const GoalTrackerDisplay: React.FC<GoalTrackerDisplayProps> = ({ tracker, title }) => {
  const isDarkMode = useIsDarkMode()
  const { locale } = useLocale()
  const { t } = useTranslate('pif')

  const isGoalAchieved = tracker.currentMilestone === tracker.target
  const fillPercent = isGoalAchieved ? 100 : Math.min((tracker.progress / (tracker.currentMilestone || 1)) * 100, 100)
  const denominator = isGoalAchieved ? tracker.target : tracker.currentMilestone

  const headerTextColor = isDarkMode ? 'white' : 'core-gray-950'
  const paragraphTextColor = isDarkMode ? 'core-gray-300' : 'core-gray-500'
  const goalText = isGoalAchieved ? t('totalTicketsGoalV6', 'Goal') : t('goalLineMilestoneText', 'Milestone')

  const formattedProgress = formatTrackerValue(tracker.progress, tracker.units, locale)
  const formattedDenominator = formatTrackerValue(denominator, tracker.units, locale)

  return (
    <div className="flex flex-col gap-2 lg:gap-6">
      {title && (
        <TitleLG color={headerTextColor} weight="bold" className="mb-2">
          {title}
        </TitleLG>
      )}
      <div className="flex flex-row justify-between lg:hidden">
        <ParagraphSM color={headerTextColor} weight="semibold">
          {formattedProgress}
        </ParagraphSM>
        <ParagraphSM color={paragraphTextColor}>
          {formattedDenominator} {goalText}
        </ParagraphSM>
      </div>
      <div className="flex flex-col gap-2 lg:gap-6">
        <div className={classNames('w-full rounded-full', isDarkMode ? 'bg-core-gray-800' : 'bg-core-gray-200')}>
          <div className="h-2.5 rounded-full bg-oxide-700" style={{ width: `${fillPercent}%` }} />
        </div>
        <div className="flex flex-row justify-between">
          <div className="hidden flex-col gap-1 lg:flex lg:gap-2">
            <TitleLG color={headerTextColor} className="hidden text-xl lg:block xl:text-2xl" weight="semibold">
              {formattedProgress}
            </TitleLG>
            <ParagraphLG color={paragraphTextColor} className="hidden text-base lg:block xl:text-lg">
              {t('ofTicketsGoalV7', 'of {{numTickets}} {{goalText}}', {
                numTickets: formattedDenominator,
                goalText,
              })}
            </ParagraphLG>
          </div>
          <div className="flex flex-col justify-end gap-1 lg:ml-auto">
            <ParagraphSM color={headerTextColor} className="block lg:hidden">
              {Math.floor(fillPercent)}%{' '}
              {isGoalAchieved ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
            </ParagraphSM>
            <TitleLG color={headerTextColor} className="hidden text-xl !font-semibold lg:flex xl:text-2xl">
              {Math.floor(fillPercent)}%{' '}
              {isGoalAchieved ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
            </TitleLG>
          </div>
        </div>
      </div>
    </div>
  )
}
